/* eslint-disable @typescript-eslint/no-explicit-any, max-lines */
import * as React from 'react';

import { Button, ButtonTheme } from '@cian/button-component';
import { plural } from '@cian/utils/lib/shared/plural';

import { HonestWork, PopupDirection } from '@cian/honestwork-component';

import {
  IAgentAvailabilitySchema,
  IAgentReviewsSchema,
  IAgentSchema,
  IKPSchema,
  INewObjectBuilderSchema,
  IOfferSchema,
  IPhoneSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { Avatar } from '../../common/components/avatar';
import { Link } from '../../common/components/link';
import { LinkWrapper } from '../../common/components/link_wrapper';
import { Tag } from '../../common/components/tag';
import { IOfferChatsState } from '../../store/offer_chats';
import {
  getAgentName,
  getTrackingLabelPrefix,
  isBroker,
  isFromBuilder,
  isHomeowner,
  isHonest,
  isPro,
  mapSkillsArrayIntoTree,
} from '../../utils/agent';
import { getFormattedPhoneNumber, getNewbuildingsListQueryStringUrl } from '../../utils/formatters';
import { getExtendedAgentBrand } from '../../utils/getExtendedAgentBrand';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { isFakeSubAgent } from '../../utils/isFakeSubAgent';
import { createAgentIdName } from '../../utils/createAgentIdName';
import { getFullProfileLink } from '../../utils/getFullProfileLink';
import { getExperienceText } from '../../utils/getExperienceText';
import { getAgentAvatar } from '../../utils/getAgentAvatar';
import { AgentPreviewContainer } from '../agent_preview';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import { PopupStartChatContainer } from '../popups/start_chat/container';
import { OwnerChecksContainer } from './owner_cheks/owner_checks_container';
import { AgentStats } from '../agent_stats';
import * as AnalyticsAside from '../contacts_aside/tracking';
import * as Analytics from './tracking';

import * as styles from './index.css';
import { isRentDepartment } from '../../utils/isRentDepartment';
import { isManagementCompany } from '../../utils/isManagementCompany';
import { isDeveloperCanCallback } from '../../utils/isDeveloperCanCallback';
import { ABCallButton } from '../ab_call_button';
import { DynamicCalltrackingButton, EPageTypes, EPlatformTypes } from '@cian/frontend-dynamic-calltracking-component';
import { PartnerTooltip } from '../PartnerTooltip';
import { isAvatarAgentRoundness } from '../../utils/isAvatarAgentRoundness';
import { CalltrackingBadgeContainer } from '../../containers/CalltrackingBadge';
import { CallSourceWishContainer } from '../../containers/CallSourceWish';

export interface IAuthorProps {
  abGroup: number;
  agent: IAgentSchema | null;
  agentReviews?: IAgentReviewsSchema | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchema;
  offerChat: IOfferChatsState;
  user: IUserSchema | null;
  agentAvailability: IAgentAvailabilitySchema | null;
  kp: IKPSchema | null;
  isAgentExperienceExperimentActive: boolean;
  showCountrysideBuilderBadge: boolean;
  companyFoundationTime: string | null;
  siteBlockId: number | null;
  newbuildingId: number | null;
  isAuthenticated: boolean;
  dynamicCalltrackingPlaceType: string;

  chatMessageSent(): void;
  openCallbackPopup(): void;
  setPhoneCollapse(): void;
}

export interface IAuthorState {
  isPhonesVisible: boolean;
  isStartChatPopupOpen: boolean;
  isOwnOfferPopupOpen: boolean;
  startChatMessage: string;
}

export class AuthorBrand extends React.PureComponent<IAuthorProps, IAuthorState> {
  public state = {
    isOwnOfferPopupOpen: false,
    isPhonesVisible: false,
    isStartChatPopupOpen: false,
    startChatMessage: '',
  };
  private botDetector = new SimpleBotDetector();

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const {
      agent,
      agentReviews,
      developer,
      offer,
      kp,
      companyFoundationTime,
      showCountrysideBuilderBadge,
    } = this.props;
    const { phones } = offer;
    const { isStartChatPopupOpen, startChatMessage, isOwnOfferPopupOpen } = this.state;

    if (!agent) {
      return null;
    }

    const { isCianPartner, isDeveloper, isSubAgent } = agent;
    const isAgentPublished = agent.status === 'published';

    const isAgentWithCompanyName = isFakeSubAgent(agent, offer);
    const isFromBuilderOffer = isFromBuilder(offer);
    const isAnHomeowner = isHomeowner(offer);
    const isHonestAgent = isHonest(agent);
    const isBrokerAgent = isBroker(agent);
    const isProAgent = isPro(agent);

    const name = isSubAgent ? agent.companyName : getAgentName(agent, offer);
    const profileLink = getFullProfileLink(agent, offer, developer);
    const experienceText = getExperienceText(agent, offer);
    const agentId = createAgentIdName(agent);

    // Если обяъвление загородки привязано к коттеджному поселку,
    // проверяем возможность отображения телефонов у застройщика.
    const shouldRenderContactButtons = kp ? kp.displayBuilderPhone : true;

    const isStatsVisible =
      isBrokerAgent || isAgentWithCompanyName || isRentDepartment(agent) || isManagementCompany(agent);
    const isAvatarRoudness = isAvatarAgentRoundness(offer, agent);
    const agentBrand = getExtendedAgentBrand(agent, offer);
    const avatar = getAgentAvatar(agent);
    const avatarHandler = profileLink ? this.handlerAgentAvatarClick : undefined;
    const nameHandler = profileLink ? this.handleAgentNameClick : undefined;

    return (
      <section className={styles['container']} id="author">
        <LinkWrapper
          target="_blank"
          notRemoveWrapper
          href={isAgentPublished ? profileLink : undefined}
          onClick={isAgentPublished ? avatarHandler : undefined}
          className={styles['link-wrapper']}
        >
          <Avatar
            url={avatar}
            className={styles['image']}
            isRoundness={isAvatarRoudness}
            character={(!isAnHomeowner && name && name[0]) || ''}
          />
        </LinkWrapper>
        <div className={styles['header']}>
          {isAgentPublished ? (
            <LinkWrapper target="_blank" href={profileLink} onClick={nameHandler} className={styles['title-link']}>
              <h2 className={styles['title']}>{name || agentId}</h2>
            </LinkWrapper>
          ) : (
            <h2 className={styles['title']}>{name || agentId}</h2>
          )}
          {isHonestAgent && (
            <HonestWork
              agentLink={{
                agentId: agent.id,
                otherLinkProps: {
                  deal_type: offer.dealType,
                  offer_type: offer.offerType,
                },
              }}
              direction={PopupDirection.RIGHT}
              className={styles['honest']}
            />
          )}
          {isProAgent && (
            <Tag className={styles['tag']} title="Профессиональный пользователь">
              Pro
            </Tag>
          )}
          {isCianPartner && (
            <div className={styles['tag']}>
              <PartnerTooltip master={<Tag className={styles['tag-partner']}>Партнёр</Tag>} />
            </div>
          )}
          {showCountrysideBuilderBadge && <Tag className={styles['tag']}>Застройщик</Tag>}
        </div>
        {agentBrand && !showCountrysideBuilderBadge && <div className={styles['prop']}>{agentBrand}</div>}
        {isStatsVisible && (
          <AgentStats large uniqueKey="broker-stats" agentReviews={agentReviews} link={`/agents/${agent.id}`} />
        )}
        {isFromBuilderOffer && companyFoundationTime && (
          <div className={styles['prop']}>Год основания: {companyFoundationTime}</div>
        )}
        {experienceText && <div className={styles['prop']}>{experienceText}</div>}
        {(!isDeveloper || isAgentWithCompanyName) && !!agent.offersCount && agent.offersCount > 1 && agent.offersLink && (
          <Link
            target="_blank"
            href={agent.offersLink}
            className={styles['offers-link']}
            onClick={this.handleQtyLinkClick}
          >
            Ещё {agent.offersCount - 1}&nbsp;
            {plural(agent.offersCount - 1, ['объект', 'объекта', 'объектов'])}
          </Link>
        )}
        {isAnHomeowner && (
          <div className={styles['owner_checks']}>
            <OwnerChecksContainer />
          </div>
        )}
        {isFromBuilderOffer && developer && developer.stats && (
          <ul className={styles['company']}>
            {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
              <li className={styles['company-item']}>
                <Link
                  className={styles['company-link']}
                  href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                  onClick={this.handleBuildingDoneClick}
                  target="_blank"
                >
                  Сдано {developer.stats.done.text}
                </Link>
              </li>
            ) : (
              <li className={styles['company-item']}>
                <span>Нет сданных ЖК</span>
              </li>
            )}
            {developer.stats.progress && developer.stats.progress.qs && developer.stats.progress.text ? (
              <li className={styles['company-item']}>
                <Link
                  className={styles['company-link']}
                  href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                  onClick={this.handleBuildingProgressClick}
                  target="_blank"
                >
                  Строится {developer.stats.progress.text}
                </Link>
              </li>
            ) : (
              <li className={styles['company-item']}>
                <span>Нет строящихся ЖК</span>
              </li>
            )}
          </ul>
        )}
        {this.renderDescription()}
        {isFromBuilderOffer && profileLink && (
          <Link
            target="_blank"
            href={profileLink}
            rel="nofollow noopener"
            className={styles['developer-link']}
            onClick={this.handleMoreLinkClick}
          >
            Подробнее о застройщике
          </Link>
        )}
        {isSubAgent && (
          <div className={styles['agent-preview']}>
            <AgentPreviewContainer onClick={this.handleAgentPreviewClick} />
          </div>
        )}
        {phones && !phones.some(phone => !phone.countryCode || !phone.number) && shouldRenderContactButtons && (
          <div className={styles['buttons']}>
            {this.renderDynamicCalltrackingButton()}
            {isDeveloperCanCallback(agent, offer.isDealRequestSubstitutionPhone) && (
              <Button theme={ButtonTheme.Ghost} onClick={this.handleDeveloperCalback}>
                Перезвоните мне
              </Button>
            )}
            {this.renderSendMessageButton()}
          </div>
        )}
        {this.state.isPhonesVisible && <CalltrackingBadgeContainer />}
        {this.state.isPhonesVisible && <CallSourceWishContainer />}
        <PopupStartChatContainer
          isOpen={isStartChatPopupOpen}
          message={startChatMessage}
          onMessageChange={this.handleMessageChange}
          onMessageSent={this.handleMessageSent}
          onClose={this.handlePopupStartChatClose}
        />
        <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={this.handlePopupOwnOfferClose} />
      </section>
    );
  }

  private renderDynamicCalltrackingButton = () => {
    const { offer, siteBlockId, dynamicCalltrackingPlaceType } = this.props;
    const { isPhonesVisible } = this.state;

    const phones = offer.phones;

    const hasPhones = phones && !phones.some(phone => !phone.countryCode || !phone.number);
    if (!hasPhones) {
      return null;
    }

    const phone = (offer.phones as IPhoneSchema[]).map(getFormattedPhoneNumber).join(', ');

    return offer.status === 'published' ? (
      <DynamicCalltrackingButton
        onClick={this.handleShowPhoneClick}
        className={styles['show-phone']}
        openPhoneClassName={styles['phones']}
        isOpen={isPhonesVisible}
        phone={phone}
        announcementId={offer.id}
        blockId={siteBlockId}
        placeType={dynamicCalltrackingPlaceType}
        platformType={EPlatformTypes.WebDesktop}
        pageType={EPageTypes.OfferCard}
      >
        <ABCallButton />
      </DynamicCalltrackingButton>
    ) : null;
  };

  private renderSendMessageButton = () => {
    const { user, offer } = this.props;
    const { isChatsEnabled, chatId, isChatMessageSent } = this.props.offerChat;
    const isOfferOwnedByUser = user && user.isAuthenticated && user.realtyUserId === offer.userId;

    return isChatsEnabled ? (
      <Button theme={ButtonTheme.Ghost} onClick={this.onStartChatButtonClick}>
        {(!!chatId || isChatMessageSent) && !isOfferOwnedByUser ? 'Посмотреть сообщения' : 'Написать сообщение'}
      </Button>
    ) : null;
  };

  private onStartChatButtonClick = () => {
    const { agentAvailability, offer, user, agent, kp } = this.props;
    const { chatId, isChatMessageSent } = this.props.offerChat;

    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
    const isAuthenticated = user && user.isAuthenticated;
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;
    Analytics.trackFirstMessageClick(offer, agent, kp && kp.id); // <- клик на кнопку!! В любом случае должен отправляться!!
    switch (true) {
      case !isAuthenticated:
        //tslint:disable-next-line:no-any
        if ((window as any).waitForLoginPopup) {
          (window as any).waitForLoginPopup('chats'); //tslint:disable-line:no-any
        }
        break;
      case Boolean(isChatMessageSent || !!chatId):
        Analytics.trackGoToMessages();
        window.location.href = `/dialogs/?offerId=${offer.id}`;
        break;
      case isOfferOwnedByUser:
        this.setState({ isOwnOfferPopupOpen: true });
        break;
      default:
        this.setState({ isStartChatPopupOpen: true });
        AnalyticsAside.trackFirstMessageShowPopup(offer, agent, isAgentNotAvailable ? 'open_message' : undefined);
    }
  };

  private openChatFromPhones = () => {
    const { offer, user, agent, agentAvailability } = this.props;
    const { isChatsEnabled } = this.props.offerChat;

    const isAuthenticated = user && user.isAuthenticated;
    const isNotAvailable = Boolean(agentAvailability && !agentAvailability.available);
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;

    if (isChatsEnabled && isNotAvailable) {
      if (isOfferOwnedByUser) {
        return this.setState({ isOwnOfferPopupOpen: true });
      }

      if (isAuthenticated) {
        this.setState({ isStartChatPopupOpen: true });
        AnalyticsAside.trackFirstMessageShowPopup(offer, agent, 'open_phone');
      } else {
        //tslint:disable-next-line:no-any
        if ((window as any).waitForLoginPopup) {
          (window as any).waitForLoginPopup(); //tslint:disable-line:no-any
        }
      }
    }
  };

  private handleShowPhoneClick = async () => {
    const { abGroup, user, kp, offer, agent, setPhoneCollapse } = this.props;

    Analytics.trackClickShowPhone(offer, agent, abGroup, user, kp && kp.id, !this.botDetector.isHuman());
    setPhoneCollapse();
    this.setState({
      isPhonesVisible: true,
    });
    this.openChatFromPhones();
  };

  private handleDeveloperCalback = () => {
    const { offer, agent, openCallbackPopup } = this.props;
    Analytics.trackClickCallbackMe(offer, agent);
    openCallbackPopup();
  };

  private handleMessageSent = () => {
    const { user, kp, offer, agent, chatMessageSent } = this.props;

    chatMessageSent();
    Analytics.trackFirstMessageSent(agent, offer, user, kp && kp.id);
  };

  private handleMessageChange = (message: string) => this.setState({ startChatMessage: message });
  private handlePopupStartChatClose = () => this.setState({ isStartChatPopupOpen: false });
  private handlePopupOwnOfferClose = () => this.setState({ isOwnOfferPopupOpen: false });
  private handleBuildingProgressClick = () => Analytics.trackGoToCard('to_developerCard_in_progress_JK');
  private handleBuildingDoneClick = () => Analytics.trackGoToCard('to_developerCard_ready_JK');
  private handleQtyLinkClick = () => Analytics.trackGoToCard(`${getTrackingLabelPrefix(this.props.offer)}qty`);
  private handleMoreLinkClick = () => Analytics.trackGoToCard('to_developerCard_more');
  private handleAgentPreviewClick = () => Analytics.trackGoToCard('to_realtorCard_sub');
  private handlerAgentAvatarClick = () => Analytics.trackGoToCard(`${getTrackingLabelPrefix(this.props.offer)}ava`);
  private handleAgentNameClick = () => Analytics.trackGoToCard(`${getTrackingLabelPrefix(this.props.offer)}name`);

  private renderDescription = () => {
    const { agent, offer } = this.props;
    let description: string | null = null;

    if (!agent) {
      return null;
    }

    if (isFromBuilder(offer)) {
      description = agent.description;
    } else {
      description = mapSkillsArrayIntoTree(agent.skills)
        .map(({ childs, name }) => `${name}: ${childs.map(child => child.name).join(', ')}`)
        .join('. ');
      if (description) {
        description = `${description}.`;
      }
    }

    if (!description) {
      return null;
    }

    if (description.length > 150) {
      description = description.substr(0, 150).concat('...');
    }

    return <div className={styles['description']}>{description}</div>;
  };
}
